import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchUsers } from "../lib/api";

const STATUS_COLORS: Record<string, string> = {
  active: "bg-green-100 text-green-700",
  trialing: "bg-blue-100 text-blue-700",
  past_due: "bg-yellow-100 text-yellow-700",
  cancelled: "bg-red-100 text-red-700",
  expired: "bg-gray-100 text-gray-600",
};

export function UsersPage() {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);

  const { data, isLoading } = useQuery({
    queryKey: ["users", search, statusFilter, page],
    queryFn: () => fetchUsers({ search, status: statusFilter, page: String(page), limit: "20" }),
  });

  const users = (data?.data?.users ?? []) as Record<string, unknown>[];
  const pagination = data?.data?.pagination;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <h1 className="text-2xl font-bold">Users</h1>
        <div className="flex gap-3">
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none"
          >
            <option value="">All Status</option>
            <option value="trialing">Trialing</option>
            <option value="active">Active</option>
            <option value="past_due">Past Due</option>
            <option value="cancelled">Cancelled</option>
            <option value="expired">Expired</option>
          </select>
          <input
            type="text"
            placeholder="Search users…"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="w-64 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none"
          />
        </div>
      </div>

      <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
        <table className="w-full text-left text-sm">
          <thead className="border-b bg-gray-50">
            <tr>
              <th className="px-4 py-3 font-medium text-gray-600">Name</th>
              <th className="px-4 py-3 font-medium text-gray-600">Email</th>
              <th className="px-4 py-3 font-medium text-gray-600">Status</th>
              <th className="px-4 py-3 font-medium text-gray-600">Type</th>
              <th className="px-4 py-3 font-medium text-gray-600">Plan</th>
              <th className="px-4 py-3 font-medium text-gray-600">Children</th>
              <th className="px-4 py-3 font-medium text-gray-600">Last Access</th>
              <th className="px-4 py-3 font-medium text-gray-600">Country</th>
              <th className="px-4 py-3 font-medium text-gray-600">IP</th>
              <th className="px-4 py-3 font-medium text-gray-600">Joined</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {isLoading ? (
              <tr><td colSpan={10} className="px-4 py-8 text-center text-gray-500">Loading…</td></tr>
            ) : users.length === 0 ? (
              <tr><td colSpan={10} className="px-4 py-8 text-center text-gray-500">No users found.</td></tr>
            ) : (
              users.map((u) => {
                const sub = Array.isArray(u.subscriptions) ? u.subscriptions[0] as Record<string, unknown> | undefined : undefined;
                const status = String(sub?.status ?? "none");
                const plan = String(sub?.plan ?? "—");
                const trialEnd = sub?.trial_ends_at ? new Date(String(sub.trial_ends_at)) : null;
                const isTrialExpiring = trialEnd && status === "trialing" && trialEnd.getTime() - Date.now() < 2 * 24 * 60 * 60 * 1000;

                return (
                  <tr key={String(u.id)} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium">{String(u.display_name ?? "—")}</td>
                    <td className="px-4 py-3 text-gray-600">{String(u.email ?? "")}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_COLORS[status] ?? "bg-gray-100 text-gray-600"}`}>
                        {status}
                        {isTrialExpiring && <span title="Trial expiring soon">⚠️</span>}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {status === "active" ? (
                        <span className="rounded-full bg-green-50 px-2.5 py-0.5 text-xs font-medium text-green-700">Premium</span>
                      ) : status === "trialing" ? (
                        <span className="rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-blue-700">Trial</span>
                      ) : (
                        <span className="rounded-full bg-purple-50 px-2.5 py-0.5 text-xs font-medium text-purple-700">Free</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-600">{plan}</td>
                    <td className="px-4 py-3">{Array.isArray(u.children) ? u.children.length : 0}</td>
                    <td className="px-4 py-3 text-gray-500 text-xs">
                      {u.last_login_at ? new Date(String(u.last_login_at)).toLocaleString() : "—"}
                    </td>
                    <td className="px-4 py-3 text-gray-500">{String(u.country ?? "—")}</td>
                    <td className="px-4 py-3 text-gray-400 text-xs font-mono">{String(u.last_login_ip ?? "—")}</td>
                    <td className="px-4 py-3 text-gray-500 text-xs">
                      {u.created_at ? new Date(String(u.created_at)).toLocaleDateString() : "—"}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-between text-sm text-gray-600">
          <span>Page {pagination.page} of {pagination.totalPages} ({pagination.total} total)</span>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="rounded border px-3 py-1 hover:bg-gray-100 disabled:opacity-40"
            >
              Previous
            </button>
            <button
              onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
              disabled={page >= pagination.totalPages}
              className="rounded border px-3 py-1 hover:bg-gray-100 disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
